import qs from 'qs';
import axiosRetry from 'axios-retry';
import axios, { AxiosError, AxiosResponse } from 'axios';

import { useAuthStore } from 'src/auth/auth.store';
import { LOCAL_API_URLS, USE_LOCAL_APIS } from 'src/config-global';

import { HttpTypes } from './types.http';
import { AxiosConnectionError } from './errors';
import { setAxiosAuthorization } from './axios-authorization-header';

export const api = axios.create({
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    serialize: (params) => qs.stringify(params, { arrayFormat: 'brackets', encode: true }),
  },
});

axiosRetry(api, { retries: 3 });

export class AxiosHttpClient implements HttpTypes.HttpClient {
  private readonly url: string = '';

  constructor(
    private readonly baseURL: string,
    private readonly interceptors?: any
  ) {
    const auth = useAuthStore.getState();
    if (auth.accessToken && api.defaults.headers.Authorization !== `Bearer ${auth.accessToken}`) {
      setAxiosAuthorization(api, auth.accessToken);
    }

    if (this.baseURL) {
      this.url = this.baseURL;
    }

    if (interceptors) {
      this.interceptors?.forEach((interceptor: ['request' | 'response', ...any[]]) => {
        const [type, ...args] = interceptor;
        api.interceptors[type].use(...args);
      });
    }
  }

  async getTokens(refreshTokenParam?: string): Promise<void> {
    let auth = useAuthStore.getState();
    const data: HttpTypes.HttpRequest = {
      url: 'auth/lp-events-new-token', // TODO: Remover isso aqui e deixar dinâmico para testes locais
      method: 'get',
      headers: {
        Authorization: `Bearer ${refreshTokenParam ?? auth.refreshToken}`,
      },
    };
    const response = await this.request(data, false);
    const { accessToken, refreshToken } = response.body as any;
    auth = useAuthStore.getState();
    if (accessToken && refreshToken) {
      auth.setSession(accessToken, refreshToken); // TODO: Em todo projeto Verificar o objeto que é retornado do back pra passar certo o valor
      setAxiosAuthorization(api, accessToken);
    } else {
      auth.clearSession();
    }
  }

  async request(
    data: HttpTypes.HttpRequest,
    retryToGetTokens: boolean = true,
    isLocalAuth?: boolean
  ): Promise<HttpTypes.HttpResponse> {
    let axiosResponse: AxiosResponse;
    let auth = useAuthStore.getState();
    let urlP = '';
    if (isLocalAuth) {
      urlP = data.url;
    } else {
      const path = data.url.replace(/^\/+/, '');
      const route = USE_LOCAL_APIS && Object.keys(LOCAL_API_URLS).find(
        (prefix) => path === prefix || path.startsWith(`${prefix}/`)
      );
      const baseURL = (route && LOCAL_API_URLS[route]) || this.url;
      urlP = `${baseURL.replace(/\/+$/, '')}/${path}`;
    }
    try {
      axiosResponse = await api.request({
        url: urlP,
        method: data.method,
        data: data.body,
        params: data.params,
        responseType: data.responseType,
        headers: {
          ...data.headers,
        },
      });
    } catch (error: any) {
      const errors = error as AxiosError;
      if (error?.response?.status === 401 && auth.refreshToken && retryToGetTokens) {
        try {
          await this.getTokens(auth.refreshToken);
          auth = useAuthStore.getState();
          if (auth.isAuthenticated) {
            return await this.request(data, false);
          }
        } catch (refereshError: any) {
          console.log('[ERROR]', refereshError.message);
        }
      }

      if (!retryToGetTokens) {
        auth.clearSession();
      }

      if (errors.response) {
        axiosResponse = errors?.response;
      } else if (errors.request) {
        axiosResponse = errors?.request;
      } else {
        axiosResponse = AxiosConnectionError as AxiosResponse;
      }
    }
    return {
      statusCode: axiosResponse.status,
      body: axiosResponse.data,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  async fakeRequest(
    data: HttpTypes.HttpRequest,
    options: HttpTypes.RequestOptions
  ): Promise<HttpTypes.HttpResponse> {
    const fakeResponse: HttpTypes.HttpResponse = {
      body: options.data,
      statusCode: options.status,
    };

    if (options.delay) {
      await new Promise((resolve) => setTimeout(resolve, options.delay));
    }

    return Promise.resolve(fakeResponse);
  }
}
